import { useEffect } from 'react';
import { Link as RouterLink, Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Link,
  Stack,
  Avatar,
  Drawer,
  Tooltip,
  Typography,
  CardActionArea,
  Button
} from '@mui/material';
// hooks
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// components
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
//
import { MHidden } from '../../components/@material-extend';
import sidebarConfig, { SidebarConfigType } from './SidebarConfig';
import { useSelector, RootState } from '../../redux/store';
import { PATH_DASHBOARD } from '../../routes/paths';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  checkModules,
  checkRoles,
  checkWorkspace,
  ROLE_END_USER,
  ROLE_USER,
  WORKSPACES_REPSOL,
  WORKSPACES_SUBMETRIKA
} from 'guards/CheckRoles';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12]
}));

// ----------------------------------------------------------------------

type IconCollapseProps = {
  onToggleCollapse: VoidFunction;
  collapseClick: boolean;
};

function IconCollapse({ onToggleCollapse, collapseClick }: IconCollapseProps) {
  return (
    <Tooltip title="Mini Menu">
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2
          })
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0
            })
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }: DashboardSidebarProps) {
  const { pathname } = useLocation();
  const { loaded, user } = useSelector((state: RootState) => state.user);
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();
  const { workspace } = useSelector((state: RootState) => state.workspace);

  const sidebarConfigRoleFiltered: SidebarConfigType = sidebarConfig
    .filter((group) => {
      if (group.roles) {
        if (!group.roles.some((role) => checkRoles(user, [role]))) {
          return false;
        }
      }
      return true;
    })
    .map((group) => {
      return {
        ...group,
        items: group.items
          .filter((item) => {
            if (item.roles?.length) {
              if (!item.roles.some((role) => checkRoles(user, [role]))) {
                return false;
              }
            }
            return true;
          })
          .filter((item) => {
            if (item.modules?.length) {
              if (!item.modules.some((role) => checkModules(workspace, [role]))) {
                return false;
              }
            }
            return true;
          })
          .filter((item) => {
            if (item.workspaces !== '') {
              if (item.workspaces !== workspace?.wsStyle) {
                return false;
              }
            }
            return true;
          })
          .filter((item) => {
            if (item.only_workspaces) {
              if (item.only_workspaces?.length > 0) {
                if (workspace) {
                  if (!item.only_workspaces.includes(workspace?.wsName)) {
                    return false;
                  }
                }
              }
            }
            return true;
          })
          .map((item) => {
            return {
              ...item,
              children: item.children?.filter((child) => {
                if (child.roles?.length) {
                  if (!child?.roles.some((role) => checkRoles(user, [role]))) {
                    return false;
                  }
                }
                return true;
              })
            };
          })
      };
    });

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const { t } = useTranslation();

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' }
      }}
      style={{ background: 'var(--navSectionBackground)' }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box
            component={RouterLink}
            to={
              checkRoles(user, ROLE_USER) ? PATH_DASHBOARD.root : PATH_DASHBOARD.end_user.dashboard
            }
            sx={{ display: 'inline-flex', width: '100%', textAlign: 'center' }}
          >
            {workspace ? (
              <img
                id="logo-navbar"
                src={workspace.wsLogo}
                alt={workspace.wsName}
                style={{
                  maxWidth: '160px',
                  margin: 'auto',
                  backgroundColor: 'white',
                  padding: 10,
                  borderRadius: 10
                }}
              />
            ) : null}
          </Box>

          <MHidden width="lgDown">
            {!isCollapse && (
              <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
            )}
          </MHidden>
        </Stack>

        {isCollapse ? (
          <Avatar
            alt="My Avatar"
            src="/static/mock-images/avatars/avatar_default.jpg"
            sx={{ mx: 'auto', mb: 2 }}
          />
        ) : (
          <Link
            underline="none"
            component={RouterLink}
            to="#"
            style={{
              backgroundColor: 'white',
              borderRadius: '10px'
            }}
          >
            <AccountStyle>
              <Avatar alt="My Avatar" src="/static/mock-images/avatars/avatar_default.jpg" />
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  {loaded && user ? user.attributes.name : '-'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px'
                  }}
                >
                  {loaded && user ? user.attributes.email : '-'}
                </Typography>
              </Box>
            </AccountStyle>
          </Link>
        )}
        <Link
          component={RouterLink}
          to={checkRoles(user, ROLE_USER) ? PATH_DASHBOARD.root : '/end-user'}
          style={{
            display: 'flex',
            color: 'white',
            marginLeft: '20px'
          }}
        >
          <img
            alt="home icon"
            src="/static/icons/ic_home.svg"
            style={{ width: `22px`, marginRight: '15px' }}
          />
          {t('home')}
        </Link>
        {workspace?.wsStyle === WORKSPACES_REPSOL && workspace?.wsName === 'repsol' && (
          <a
            href={'https://trackgas.es/app/login'}
            rel="noreferrer"
            target="_blank"
            style={{
              display: 'flex',
              color: 'white',
              marginLeft: '20px',
              textDecoration: 'none'
            }}
          >
            <img
              alt="trackgas icon"
              src="/static/icons/giraf/trackgas.svg"
              style={{ width: `22px`, marginRight: '15px' }}
            />
            {t('trackgas')}
          </a>
        )}
      </Stack>

      <NavSection navConfig={sidebarConfigRoleFiltered} isShow={!isCollapse} />
      {checkRoles(user, ROLE_USER) && (
        <Button
          style={{
            backgroundColor: '#FF6200',
            color: 'white',
            margin: '20px'
          }}
          variant={'contained'}
          component={NavLink}
          to={PATH_DASHBOARD.devices.new}
        >
          {t('device_add')}
        </Button>
      )}

      {checkRoles(user, ROLE_END_USER) && checkModules(workspace, ['orders']) && (
        <Button
          style={{
            backgroundColor: checkWorkspace(workspace, WORKSPACES_REPSOL) ? 'gray' : '#FF6200',
            color: 'white',
            margin: '20px'
          }}
          variant={'contained'}
          component={NavLink}
          to={PATH_DASHBOARD.end_user.deliver.new}
          disabled={checkWorkspace(workspace, WORKSPACES_REPSOL) ? true : false}
        >
          {t('request_delivery')}
        </Button>
      )}

      <div style={{ height: '50px' }}>&nbsp;</div>
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
